export * from "ce/plugins/Linting/utils/getInvalidModuleInputsError";
import { MODULE_TYPE } from "@appsmith/constants/ModuleConstants";
import type { ModuleInstance } from "@appsmith/constants/ModuleInstanceConstants";
import type { DataTreeEntityObject } from "@appsmith/entities/DataTree/types";
import type { InvalidModuleInputsErrorProps } from "ce/plugins/Linting/utils/getInvalidModuleInputsError";
import { default as ce_getInvalidModuleInputsError } from "ce/plugins/Linting/utils/getInvalidModuleInputsError";
import { ENTITY_TYPE } from "entities/DataTree/dataTreeFactory";
import type { DataTree } from "entities/DataTree/dataTreeTypes";
import {
  CUSTOM_LINT_ERRORS,
  CustomLintErrorCode,
} from "plugins/Linting/constants";
import getLintSeverity from "plugins/Linting/utils/getLintSeverity";
import type { LintError } from "utils/DynamicBindingUtils";
import { PropertyEvaluationErrorType } from "utils/DynamicBindingUtils";

function isModuleInstance(
  entity?: DataTreeEntityObject | ModuleInstance,
): entity is ModuleInstance {
  return Boolean(
    entity &&
      "ENTITY_TYPE" in entity &&
      entity.ENTITY_TYPE === ENTITY_TYPE.MODULE_INSTANCE,
  );
}

function getInvalidModuleInputsError(props: InvalidModuleInputsErrorProps) {
  const ceErrors = ce_getInvalidModuleInputsError(props);

  const { data, memberCallExpressions, originalBinding, script, scriptPos } =
    props;
  const errors: LintError[] = [];

  memberCallExpressions.forEach((callExpression) => {
    const { object, parentNode } = callExpression;
    if ("name" in object) {
      const name = object.name as string;
      const entity = data[name] as DataTree[string];

      if (isModuleInstance(entity) && entity.type === MODULE_TYPE.QUERY) {
        const inputs = Object.values(entity?.moduleInputs || {}).map(
          ({ label }) => label,
        );

        const paramsNode = parentNode.arguments[0];

        if (paramsNode && paramsNode.type === "ObjectExpression") {
          paramsNode.properties.forEach((prop) => {
            if (
              "name" in prop.key &&
              !inputs.includes(prop.key.name) &&
              prop.loc
            ) {
              // line position received after AST parsing is 1 more than the actual line of code, hence we subtract 1 to get the actual line number
              const propStartLine = prop.loc.start.line - 1;

              // AST parsing start column position from index 0 whereas codemirror start ch position from index 1, hence we add 1 to get the actual ch position
              const propStartCol = prop.loc.start.column + 1;
              const lintErrorMessage = CUSTOM_LINT_ERRORS[
                CustomLintErrorCode.INVALID_INPUTS
              ](inputs, prop.key.name);

              errors.push({
                errorType: PropertyEvaluationErrorType.LINT,
                raw: script,
                severity: getLintSeverity(
                  CustomLintErrorCode.INVALID_INPUTS,
                  lintErrorMessage,
                ),
                errorMessage: {
                  name: "LintingError",
                  message: lintErrorMessage,
                },
                errorSegment: prop.key.name,
                originalBinding,
                code: CustomLintErrorCode.INVALID_INPUTS,
                line: propStartLine - scriptPos.line,
                ch:
                  propStartLine === scriptPos.line
                    ? propStartCol - scriptPos.ch
                    : propStartCol,
                variables: [prop.key.name],
              });
            }
          });
        }
      }
    }
  });

  return [...ceErrors, ...errors];
}

export default getInvalidModuleInputsError;
